import React, { useContext, useEffect, useState } from "react";
import { wsModalContext } from "./WSModal.context";
import { WSModal } from "./WSModal.component";
import {
  buildModalKey,
  OpenModalComponentProps,
  OptionalModalComponentProps,
  RequiredModalComponentProps,
  WSModalProps
} from "./utils";

type WithArgumentsOpen<Props extends {}, Result extends any> = (
  props: OpenModalComponentProps<Props>,
  modalProps?: WSModalProps
) => Promise<Result | null>;

type EmptyArgumentsOpen<Props extends {}, Result extends any> = (
  props?: OptionalModalComponentProps<Props>,
  modalProps?: WSModalProps
) => Promise<Result | null>;

export type OpenModalCallback<Props extends {}, Result extends any> = {
  onClose: (result: Result) => void;
} extends RequiredModalComponentProps<Props>
  ? EmptyArgumentsOpen<Props, Result>
  : WithArgumentsOpen<Props, Result>;

export type UseWSModalResult<Props extends {}, Result extends any> = {
  open: OpenModalCallback<Props, Result>;
  close: () => void;
  isOpened: boolean;
};

export type ModalComponent<Props extends {}, Result extends any> = React.FC<
  Props & {
    onClose: (props: Result) => void;
  }
>;

export const useWSModal = <Props extends {}, Result extends any>(
  Component: ModalComponent<Props, Result>,
  initialModalProps: WSModalProps<Result> = {}
): UseWSModalResult<Props, Result> => {
  const { createModal, removeModal, modals } = useContext(wsModalContext);

  const [key] = useState(() => buildModalKey());

  useEffect(
    () => () => {
      removeModal(key);
    },
    [key, removeModal]
  );

  return {
    isOpened: modals.some((modal) => modal.key === key),
    open: ((props = {} as Props, modalProps: WSModalProps<Result> = {}) =>
      new Promise<Result>((resolve) => {
        createModal({
          key,
          modal: () => (
            <WSModal {...initialModalProps} {...modalProps} onClose={resolve}>
              <Component {...props} onClose={resolve} />
            </WSModal>
          )
        });
      }).then((result) => {
        modalProps?.onClose?.(result);
        initialModalProps.onClose?.(result);
        removeModal(key);
        return result;
      })) as OpenModalCallback<Props, Result>,
    close: () => {
      removeModal(key);
    }
  };
};
